import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { HeadlineLarge, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import TextField from '@shared/ui/atoms/TextField';
import AccessRequired from '../components/AccessRequired';
import { redirectTo, selectCurrentUser } from '@shared/store/auth.duck';
import { SecondaryButton } from '@shared/ui/atoms/buttons/SecondaryButton';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { isCompanyAdminUser } from '@shared/utils/AccessControl';
import { Column } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { useTranslation } from 'react-i18next';
import { loadConfig } from '@shared/store/config.duck';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { selectAllDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';
import { deleteDepartment, requestUpdateDepartment } from '@features/whereabouts/pages/departments/data/departmentsService';
import { UserInfoV2 } from '@features/settings/pages/manage-users-page-v2/ducks/ManageUsers.model';
import { getAllUsersInDepartment } from './departmentUsersService';
import { DepartmentMemberRow } from './components/DepartmentMemberRow';

const DEPARTMENTS_URL = '/manage/departments';

export default function EditDepartmentPage() {
  const params: any = useParams();
  return <EditDepartmentView departmentId={parseInt(params.departmentId)} />;
}

interface EditDepartmentViewProps {
  departmentId: number;
  // When set, the view is embedded in another page: going back, saving,
  // deleting or a stale id return control to the host instead of navigating.
  onClose?: () => void;
}

export function EditDepartmentView({ departmentId, onClose }: EditDepartmentViewProps) {
  const dispatch = useDispatch();
  const current = useSelector(selectCurrentUser);
  const departments = useSelector(selectAllDepartments);
  const canManage = useMemo(() => isCompanyAdminUser(current), [current]);
  const { t } = useTranslation();

  const department = useMemo(
    () => departments.find(d => d.id === departmentId),
    [departments, departmentId],
  );

  const returnToList = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(redirectTo(DEPARTMENTS_URL));
    }
  };

  const [name, setName] = useState('');
  const [members, setMembers] = useState<UserInfoV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  // A department can only be deleted once it has no members. Check both the
  // config count and the live list so removals reflect immediately.
  const hasMembers = (department?.count ?? 0) > 0 || members.length > 0;

  useEffect(() => {
    if (department) {
      setName(department.name);
    }
  }, [department]);

  useEffect(() => {
    // Departments come from config; if they haven't loaded yet, fetch them.
    // If they have loaded but this id isn't among them, the link is stale.
    if (!department) {
      if (departments.length === 0) {
        dispatch(loadConfig());
      } else {
        returnToList();
      }
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAllUsersInDepartment(department.id)
      .then(users => { if (!cancelled) setMembers(users); })
      .catch(() => { if (!cancelled) setMembers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [department, departments.length, dispatch, reloadToken]);

  const saveChanges = async () => {
    if (!department) return;
    setLoading(true);
    try {
      await requestUpdateDepartment({ ...department, name });
      dispatch(loadConfig());
      dispatch(successNotification('Department updated'));
      returnToList();
    } catch {
      dispatch(failureNotification('Department failed to update'));
      setLoading(false);
    }
  };

  const onMemberRemoved = (userId: string) => {
    setMembers(prev => prev.filter(u => u.id !== userId));
    // Keep department member counts in sync (used to gate deletion).
    dispatch(loadConfig());
  };

  const openAddMembers = () => {
    if (!department) return;
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.AddDepartmentMembers,
      payload: {
        department,
        // Refresh the list and counts once the dialog closes.
        onClose: () => {
          setReloadToken(token => token + 1);
          dispatch(loadConfig());
        },
      },
    }));
  };

  const onDelete = async () => {
    // IconButton styles a disabled state but still fires onClick, so guard here:
    // a department can only be deleted once it has no members.
    if (!department || hasMembers) return;
    setLoading(true);
    try {
      await deleteDepartment(department);
      dispatch(loadConfig());
      dispatch(successNotification('Department deleted'));
      returnToList();
    } catch {
      dispatch(failureNotification('Failed to delete department'));
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      {loading && <LoadingSpinner fullScreen={true} />}
      <div className="management">

        <GoBackButton onClick={onClose} url={DEPARTMENTS_URL} />
        <HeadlineLarge className="management__title">{t('department-dialog.title')}</HeadlineLarge>
        <AccessRequired visible={!canManage} companyAdminsRequired={true} />

        <Column className="management__row">
          <TextField dataTest="editDepartmentPage-name-input"
                     label={'department-dialog.name'}
                     value={name}
                     onChange={setName}
                     disabled={!canManage} />

          {canManage &&
            <SecondaryButton dataTest="editDepartmentPage-save"
                             size={ButtonSize.Large}
                             text="button.save-changes"
                             fullWidth={false}
                             click={saveChanges} />}
        </Column>

        <div className="editTeam__membersHeader">
          <HeadlineSmall className="management__row">{t('members')}</HeadlineSmall>
          {canManage &&
            <OutlineButton dataTest="editDepartmentPage-addMembers"
                           className="management__row"
                           size={ButtonSize.Medium}
                           text="settings.departments.add-members"
                           click={openAddMembers} />}
        </div>
        {members.map(user => (
          <DepartmentMemberRow key={user.id}
                               user={user}
                               canRemove={canManage}
                               onRemoved={onMemberRemoved} />
        ))}

        {canManage &&
          <div className="management__loneControl">
            <IconButton color="darkGrey"
                        icon={IconTypes.Bin}
                        text="button.delete"
                        disabled={hasMembers}
                        onClick={onDelete} />
          </div>}

      </div>
    </React.Fragment>
  );
}
